import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import LoginGate from '@/components/ui/LoginGate';
import { useDemoV2 } from '@/features/demo/hooks/useDemoV2';
import { useAuth } from '@/hooks/useAuth';
import { useSession } from '@/lib/hooks/useSession';
import { useRole, type StaffRole } from '@/hooks/useRole';
import { tenantPath } from '@/lib/urls';
import { useTenantSlug } from '@/stores/tenantStore';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: StaffRole | StaffRole[];
  requiredPermission?: string;
  requireAuth?: boolean;
  allowDemo?: boolean;
  legacyPage?: string | string[];
  customAccessCheck?: (permissions: string[]) => boolean;
}

const readDemoSessionFromStorage = (expectedSlug: string | null): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const slug = window.localStorage.getItem('braise:demo_slug_v2');
    const jwt = window.localStorage.getItem('braise:demo_jwt_v2');
    const expiresAt = window.localStorage.getItem('braise:demo_expires_at_v2');

    if (!slug || !jwt || !expiresAt) {
      return false;
    }

    if (expectedSlug && slug !== expectedSlug) {
      return false;
    }

    const expires = new Date(expiresAt);
    return expires > new Date();
  } catch (error) {
    console.warn('[ProtectedRouteV2] demo storage read failed:', error);
    return false;
  }
};

const WATCHDOG_TIMEOUT_MS = 2_000;

export const ProtectedRouteV2 = ({
  children,
  requiredRole,
  requiredPermission,
  requireAuth = true,
  allowDemo = false,
  legacyPage,
  customAccessCheck,
}: ProtectedRouteProps): React.ReactElement | null => {
  const { user } = useAuth();
  const sessionQuery = useSession();
  const { staffInfo, loading: roleLoading, hasRole, hasPermission, permissions } = useRole();
  const { isDemo, slug: demoSlug, isExpired } = useDemoV2();
  const location = useLocation();

  const session = sessionQuery.data;
  const sessionLoading = sessionQuery.loading;
  const authenticated = sessionQuery.authenticated;
  const sessionUser = session?.user ?? null;
  const effectiveUser = user ?? sessionUser ?? null;

  const [watchdogFired, setWatchdogFired] = useState(false);
  const watchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debugOnceRef = useRef(new Set<string>());
  const debugOnce = useCallback(
    (key: string, message: string, payload?: Record<string, unknown>) => {
      if (!import.meta.env.DEV) {
        return;
      }

      const composite = `${location.pathname || '/'}:${key}`;
      if (debugOnceRef.current.has(composite)) {
        return;
      }

      debugOnceRef.current.add(composite);

      if (payload) {
        console.debug(message, payload);
      } else {
        console.debug(message);
      }
    },
    [location.pathname],
  );

  const authOnly = requireAuth && !requiredPermission;
  const loginNext = useMemo(() => {
    const nextPath = location.pathname + (location.search || '');
    return nextPath && nextPath.length > 0 ? nextPath : '/';
  }, [location.pathname, location.search]);
  const isPublicTableRoute = /^\/r\/[^/]+\/table\/[^/]+/.test(location.pathname);
  const { subdomain } = useParams();
  const tenantSlug = useTenantSlug();
  const normalizedSlug = subdomain ?? null;

  let urlDemoMode = false;
  let contextDemoActive = false;
  let storageDemoActive = false;

  if (!isPublicTableRoute) {
    try {
      urlDemoMode = new URLSearchParams(location.search).get('mode') === 'demo';
    } catch (error) {
      console.warn('[ProtectedRouteV2] unable to parse search params:', error);
    }

    contextDemoActive =
      allowDemo && isDemo && !isExpired && (!normalizedSlug || demoSlug === normalizedSlug);
    storageDemoActive = allowDemo && readDemoSessionFromStorage(normalizedSlug);
  }

  const demoBypassActive = allowDemo && (contextDemoActive || storageDemoActive || urlDemoMode);
  const isDemoRoute = allowDemo && (urlDemoMode || contextDemoActive || storageDemoActive);
  const guardEnabled = requireAuth && !demoBypassActive;

  useEffect(() => {
    if (isPublicTableRoute || typeof window === 'undefined') {
      return;
    }

    if (!guardEnabled || !sessionLoading) {
      if (watchdogTimerRef.current) {
        clearTimeout(watchdogTimerRef.current);
        watchdogTimerRef.current = null;
      }
      if (watchdogFired) {
        setWatchdogFired(false);
      }
      return;
    }

    if (watchdogTimerRef.current) {
      return;
    }

    watchdogTimerRef.current = window.setTimeout(() => {
      setWatchdogFired(true);
      debugOnce('session-watchdog', '[ProtectedRouteV2] auth watchdog fired after 2000ms', {
        path: location.pathname,
      });
      watchdogTimerRef.current = null;
    }, WATCHDOG_TIMEOUT_MS);

    return () => {
      if (watchdogTimerRef.current) {
        clearTimeout(watchdogTimerRef.current);
        watchdogTimerRef.current = null;
      }
    };
  }, [isPublicTableRoute, guardEnabled, sessionLoading, location.pathname, watchdogFired, debugOnce]);

  if (isPublicTableRoute) {
    return <>{children}</>;
  }

  const loadingView = (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>
  );

  const normalizedPath = location.pathname.replace(/\/+$/, '').replace(/^\/+/, '');
  const effectiveSlug = tenantSlug ?? normalizedSlug ?? null;
  const sanitizedLegacyPages = legacyPage
    ? (Array.isArray(legacyPage) ? legacyPage : [legacyPage])
        .map((page) => page.replace(/^\/+/, '').replace(/\/+$/, ''))
        .filter((page): page is string => Boolean(page))
    : [];

  if (!demoBypassActive && normalizedPath === 'reports' && effectiveSlug && !normalizedSlug) {
    debugOnce('redirect-legacy-reports', '[ProtectedRouteV2] redirect legacy reports', {
      effectiveSlug,
      normalizedPath,
    });
    return <Navigate to={tenantPath(effectiveSlug, 'reports')} replace />;
  }

  if (!demoBypassActive && sanitizedLegacyPages.length > 0 && effectiveSlug && !normalizedSlug) {
    const matchedLegacyPage = sanitizedLegacyPages.find((page) => page === normalizedPath);
    if (matchedLegacyPage) {
      return <Navigate to={tenantPath(effectiveSlug, matchedLegacyPage)} replace />;
    }
  }

  if (requireAuth) {
    if (!demoBypassActive) {
      if (sessionLoading && !watchdogFired) {
        debugOnce('awaiting-auth', '[ProtectedRouteV2] awaiting authentication', {
          path: location.pathname,
          requireAuth,
          allowDemo,
        });
        return loadingView;
      }

      if (!authenticated) {
        debugOnce(
          watchdogFired ? 'auth-watchdog' : 'login-gate',
          watchdogFired
            ? '[ProtectedRouteV2] watchdog resolved unauthenticated; showing login gate'
            : '[ProtectedRouteV2] unauthenticated session; showing login gate',
          {
            path: location.pathname,
            requireAuth,
            allowDemo,
          },
        );
        return <LoginGate next={loginNext} />;
      }

      if (authOnly) {
        return <>{children}</>;
      }
    }
  }

  if (isDemoRoute) {
    if (demoBypassActive) {
      return <>{children}</>;
    }

    if (isDemo && demoSlug !== normalizedSlug) {
      return <Navigate to="/" replace />;
    }

    if (!isDemo && requireAuth && !effectiveUser) {
      debugOnce('demo-login', '[ProtectedRouteV2] demo route requires login', {
        path: location.pathname,
      });
      return <LoginGate next={loginNext} />;
    }
  }

  if (requireAuth && !effectiveUser && !demoBypassActive) {
    debugOnce('redirect-login', '[ProtectedRouteV2] redirecting to login gate', {
      path: location.pathname,
    });
    return <LoginGate next={loginNext} />;
  }

  if (guardEnabled && !authOnly && roleLoading) {
    debugOnce('loading-role', '[ProtectedRouteV2] awaiting role resolution', {
      path: location.pathname,
      requireAuth,
      allowDemo,
    });
    return loadingView;
  }

  if (customAccessCheck && !customAccessCheck(permissions) && !demoBypassActive) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert className="max-w-md">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to access this page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (requireAuth && requiredRole && !hasRole(requiredRole) && !demoBypassActive) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert className="max-w-md">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to access this page. Required role: {
              Array.isArray(requiredRole) ? requiredRole.join(' or ') : requiredRole
            }
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (requireAuth && requiredPermission && !hasPermission(requiredPermission) && !demoBypassActive) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert className="max-w-md">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to access this page. Required permission: {requiredPermission}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (
    requireAuth &&
    effectiveUser &&
    (requiredRole || requiredPermission) &&
    !demoBypassActive &&
    !staffInfo
  ) {
    debugOnce('awaiting-staff-info', '[ProtectedRouteV2] waiting for staff details before rendering', {
      path: location.pathname,
      requireAuth,
      allowDemo,
    });
    return loadingView;
  }

  return <>{children}</>;
};
